import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const CONTAINER = "min-h-screen bg-[#0b1220] text-slate-300 p-6 md:p-10 max-w-3xl mx-auto";
const H1 = "text-2xl md:text-3xl font-medium tracking-tight text-slate-100 mb-2";
const H2 = "text-sm uppercase tracking-[0.2em] text-blue-300 mt-8 mb-3";
const P = "text-sm leading-relaxed text-slate-400 mb-3";
const LI = "text-sm leading-relaxed text-slate-400 mb-1 ml-4 list-disc";

function BackLink() {
  return (
    <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-slate-500 hover:text-blue-400 mb-6">
      <ArrowLeft size={14} /> Back
    </Link>
  );
}

export function PrivacyPolicy() {
  return (
    <div className={CONTAINER} data-testid="privacy-policy">
      <BackLink />
      <h1 className={H1}>Privacy Policy</h1>
      <p className="text-xs text-slate-500 mb-8">Last updated: 24 July 2026</p>

      <h2 className={H2}>What we collect</h2>
      <ul className="mb-3">
        <li className={LI}>Google-provided email, name, profile picture (for sign-in).</li>
        <li className={LI}>Zerodha broker user id + encrypted access token (never the password).</li>
        <li className={LI}>Optional TOTP seed (Fernet AES-128 encrypted at rest; never returned by any API).</li>
        <li className={LI}>Your paper-trade history, strategy overrides, and app-usage audit log.</li>
        <li className={LI}>Market data snapshots (candles, option quotes) from Zerodha for analytics.</li>
      </ul>

      <h2 className={H2}>What we do NOT collect or store</h2>
      <ul className="mb-3">
        <li className={LI}>Your Zerodha password. This is enforced by design — Zerodha's login page is hosted by Zerodha; we never receive it.</li>
        <li className={LI}>Payment or banking information. This app processes zero money.</li>
        <li className={LI}>Third-party trackers or advertising cookies.</li>
      </ul>

      <h2 className={H2}>How your data is used</h2>
      <p className={P}>
        Data is used solely to render your dashboard, compute signals, execute paper trades, and send you the Telegram digests you subscribe to. We do not sell, share, or aggregate your data with third parties. No LLM ingest, no marketing profiling.
      </p>

      <h2 className={H2}>Where it lives</h2>
      <p className={P}>
        MongoDB Atlas (India region) with encryption-at-rest enabled. Sensitive fields (broker tokens, TOTP seed) are additionally application-encrypted with Fernet before insertion. Backups are retained for 7 days.
      </p>

      <h2 className={H2}>Your rights (DPDPA 2023 / GDPR)</h2>
      <ul className="mb-3">
        <li className={LI}>Right to access — request a JSON export of all your data.</li>
        <li className={LI}>Right to correction — edit strategy overrides + broker connection anytime.</li>
        <li className={LI}>Right to erasure — email us and we will delete your account within 7 days.</li>
        <li className={LI}>Right to withdraw consent — disconnect Zerodha + remove TOTP seed from the Dashboard.</li>
      </ul>

      <h2 className={H2}>Contact</h2>
      <p className={P}>
        For any data-privacy request or grievance: <a className="text-blue-300 hover:underline" href="mailto:privacy@myedgetrader.com">privacy@myedgetrader.com</a>.
      </p>

      <p className="text-[10px] text-slate-600 mt-10 leading-relaxed">
        This app is a paper-trading and observability tool. It is not a broker, does not place live orders, and is not registered with SEBI as a research analyst or investment advisor. Nothing shown constitutes investment advice.
      </p>
    </div>
  );
}

export function TermsOfService() {
  return (
    <div className={CONTAINER} data-testid="terms-of-service">
      <BackLink />
      <h1 className={H1}>Terms of Service</h1>
      <p className="text-xs text-slate-500 mb-8">Last updated: 24 July 2026</p>

      <h2 className={H2}>What MyEdge Trader is</h2>
      <p className={P}>
        MyEdge Trader is a personal-use paper-trading and analytics dashboard for intraday NIFTY/BANKNIFTY options built on the Zerodha Kite Connect API. It computes signals from live market data and records simulated trades. It does NOT place real orders.
      </p>

      <h2 className={H2}>What it is NOT</h2>
      <ul className="mb-3">
        <li className={LI}>It is <b>not</b> a SEBI-registered investment advisor.</li>
        <li className={LI}>It is <b>not</b> a broker. All actual order placement happens on the Zerodha app.</li>
        <li className={LI}>Signals and back-tests are <b>not</b> guarantees of live-trading performance.</li>
      </ul>

      <h2 className={H2}>Your responsibilities</h2>
      <ul className="mb-3">
        <li className={LI}>You are solely responsible for all trades you place on Zerodha or any other broker, whether inspired by this app or not.</li>
        <li className={LI}>You must comply with Zerodha's Terms of Service, SEBI regulations, and applicable tax laws in your jurisdiction.</li>
        <li className={LI}>You must safeguard your Zerodha password. We deliberately do not accept or store it.</li>
        <li className={LI}>You must not use this app to run unattended live-trading strategies without independent human oversight, unless local regulations permit.</li>
      </ul>

      <h2 className={H2}>Limitation of liability</h2>
      <p className={P}>
        The app is provided "as-is" without any warranty of accuracy, fitness, or availability. Under no circumstances shall the maintainers be liable for any direct, indirect, incidental, consequential, or trading losses arising out of your use of the app, including but not limited to signal errors, missed exits, or Kite API outages.
      </p>

      <h2 className={H2}>Fair use</h2>
      <p className={P}>
        Do not attempt to reverse engineer, brute-force, or overload the API. Do not use the app to violate Zerodha's Kite Connect T&C (specifically the ban on storing broker passwords or automating login flows beyond the 2FA seed helper we provide).
      </p>

      <h2 className={H2}>Contact</h2>
      <p className={P}>
        Grievances or questions: <a className="text-blue-300 hover:underline" href="mailto:support@myedgetrader.com">support@myedgetrader.com</a>.
      </p>
    </div>
  );
}
